import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { api, setUserInfo } from '../api/client';
import '../App.css';
import { translateError } from '../utils/errorMessages';

interface OrgSwitcherProps {
  className?: string;
}

const OrgSwitcher: React.FC<OrgSwitcherProps> = ({ className = '' }) => {
  const { t } = useTranslation();
  const { user, currentMembership } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get active memberships (excluding current)
  const memberships = user?.memberships?.filter(m => m.isActive) || [];
  const otherMemberships = memberships.filter(
    m => m.organization._id !== currentMembership?.organization._id
  );

  const handleSwitchOrg = async (orgId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      // Call API to switch organization and get new token
      const response = await api.auth.switchOrg(orgId);
      const userData = response.data as any;
      setIsOpen(false);

      // Find the target organization's slug
      const newMembership = memberships.find(m => m.organization._id === orgId);
      if (newMembership) {
        const newSlug = newMembership.organization.slug;
        const currentHost = window.location.hostname;

        // Check if we're switching to a different subdomain
        const needsCrossSubdomainAuth =
          currentHost.includes('.localhost') ||
          currentHost.endsWith('.eventik.up.railway.app') ||
          currentHost.endsWith('.eventik.cz');

        if (needsCrossSubdomainAuth) {
          // Hand off the token only. The destination rebuilds user + memberships
          // from GET /api/users/session, so nothing about the user rides in the
          // URL — no base64 of names (btoa throws on non-Latin-1 like "ZŠ"), and
          // no client-supplied identity for the destination to trust.
          const token = encodeURIComponent(userData.token);
          const port = window.location.port ? `:${window.location.port}` : '';
          let newHost: string;

          if (currentHost.includes('.localhost')) {
            newHost = `${newSlug}.localhost${port}`;
          } else if (currentHost.endsWith('.eventik.up.railway.app')) {
            newHost = `${newSlug}.eventik.up.railway.app`;
          } else if (currentHost.endsWith('.eventik.cz') || currentHost === 'eventik.cz') {
            newHost = `${newSlug}.eventik.cz`;
          } else {
            // Generic: replace the leftmost label, or prepend one.
            const parts = currentHost.split('.');
            if (parts.length >= 3) {
              parts[0] = newSlug;
            } else {
              parts.unshift(newSlug);
            }
            newHost = parts.join('.') + port;
          }

          const protocol = currentHost.includes('.localhost') ? 'http:' : window.location.protocol;
          window.location.href = `${protocol}//${newHost}/auth/org-switch?token=${token}`;
        } else {
          // Same origin (plain localhost, an IP, or any host without a tenant
          // subdomain): the tenant is carried by devTenantSlug, so just persist
          // and reload. Previously anything that was neither a known subdomain
          // pattern nor localhost fell through with no branch at all, leaving
          // the switcher spinning forever.
          setUserInfo(userData);
          localStorage.setItem('devTenantSlug', newSlug);
          window.location.href = '/admin/calendar';
        }
      } else {
        setError(t('common.error'));
        setIsLoading(false);
      }
    } catch (err: any) {
      // err.message matters here: a client-side throw has no `response`, and
      // swallowing it into a generic message is what hid this bug.
      setError(translateError(err, t, 'common.error'));
      setIsLoading(false);
    }
  };

  // Don't render if user has only one membership (or none)
  if (!user || memberships.length <= 1) {
    return null;
  }

  return (
    <div className={`org-switcher ${className}`} ref={dropdownRef}>
      <button
        className="org-switcher-toggle"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="org-switcher-current">
          {currentMembership?.organization.name || t('common.selectOrganization')}
        </span>
        <span className="org-switcher-arrow">{isOpen ? '\u25B4' : '\u25BE'}</span>
      </button>

      {isOpen && (
        <div className="org-switcher-dropdown" role="listbox">
          {error && (
            <div className="org-switcher-error">{error}</div>
          )}

          <div className="org-switcher-header">
            {t('common.switchOrganization')}
          </div>

          <div className="org-switcher-list">
            {otherMemberships.map((membership) => (
              <button
                key={membership.organization._id}
                className="org-switcher-item"
                onClick={() => handleSwitchOrg(membership.organization._id)}
                disabled={isLoading}
                role="option"
                aria-selected={false}
              >
                <span className="org-switcher-item-name">
                  {membership.organization.name}
                </span>
                <span className="org-switcher-item-role">
                  {membership.role.name}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrgSwitcher;
